<template>
  <div class="min-h-screen px-5 py-6 md:px-6">
    <div class="w-full space-y-5">
      <!-- Standard Page Header -->
      <section
        class="rounded-[1.5rem] border border-[#d8e4f0] bg-white p-5 shadow-[0_14px_30px_rgba(15,23,42,0.05)]"
      >
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
        >
          <div class="space-y-2">
            <span
              class="inline-flex rounded-full bg-[#eaf3fb] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab]"
            >
              Reports
            </span>
            <div>
              <h1 class="text-[2rem] font-black leading-tight text-[#0f172a]">
                Reports
              </h1>
              <p class="mt-2 max-w-3xl text-[0.95rem] text-[#64748b]">
                Review your account’s monthly performance, progress updates, and
                downloadable reports.
              </p>
            </div>
          </div>

          <div class="flex flex-wrap gap-3">
            <button
              class="action-btn action-btn--light"
              @click="refresh"
            >
              <RefreshCw
                class="h-4 w-4"
                :class="{ 'animate-spin': loading }"
              />
              Refresh
            </button>
            <button
              v-if="canGenerateReports"
              class="action-btn action-btn--dark"
              @click="openBuilder()"
            >
              <Plus class="h-4 w-4" />
              Generate Report
            </button>
          </div>
        </div>
      </section>

      <!-- Error banner -->
      <section
        v-if="error"
        class="rounded-[1rem] border border-[#f5c2c7] bg-[#fff5f5] px-4 py-3 text-[#b42318]"
      >
        {{ error }}
      </section>

      <!-- Loading skeleton -->
      <section
        v-if="loading"
        class="grid gap-5 lg:grid-cols-[360px_1fr] xl:grid-cols-[400px_1fr]"
      >
        <div class="rounded-[1.4rem] border border-[#d8e4f0] bg-white p-5 shadow-[0_10px_22px_rgba(15,23,42,0.04)] space-y-4">
          <div class="h-5 w-24 bg-slate-200 animate-pulse rounded" />
          <div class="h-10 w-full bg-slate-100 animate-pulse rounded-[0.95rem]" />
          <div class="h-9 w-full bg-slate-100 animate-pulse rounded-[0.95rem]" />
          <div class="space-y-2.5 pt-2">
            <div
              v-for="i in 5"
              :key="i"
              class="h-16 bg-slate-50 animate-pulse rounded-[1rem]"
            />
          </div>
        </div>
        <div class="space-y-4">
          <div class="h-28 rounded-[1.4rem] border border-[#d8e4f0] bg-white p-6 animate-pulse" />
          <div class="h-64 rounded-[1.4rem] border border-[#d8e4f0] bg-white p-6 animate-pulse" />
        </div>
      </section>

      <!-- Client Role View (Full Width) -->
      <ClientReportsView
        v-else-if="currentUserRole === 'client'"
        :client="clientAccount"
        :slots="clientDisplaySlots"
        :status-meta="slotStatusMeta"
        :can-view="canViewReports"
        :can-download="canExportReportPdf"
        @view-report="openPreviewById"
        @download-pdf="openPdfById"
      />

      <!-- Admin / Staff Two-Column Master-Detail Layout -->
      <div
        v-else
        class="grid gap-5 lg:grid-cols-[360px_1fr] xl:grid-cols-[400px_1fr] items-start"
      >
        <!-- Left Column: Clients List -->
        <aside
          class="rounded-[1.4rem] border border-[#d8e4f0] bg-white p-5 shadow-[0_10px_22px_rgba(15,23,42,0.04)] space-y-4"
          :class="{ 'hidden lg:block': mobileShowingDetails && activeClient }"
        >
          <div class="flex items-center justify-between pb-1">
            <h2 class="text-[1.15rem] font-extrabold text-[#0f172a]">
              Clients
            </h2>
            <span class="inline-flex rounded-full bg-[#eaf3fb] px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab]">
              {{ filteredClients.length }}
            </span>
          </div>

          <!-- Compact Search & Dropdown Filters -->
          <ReportFilters
            :search="search"
            :status-filter="statusFilter"
            :service-filter="serviceFilter"
            :service-options="serviceFilterOptions"
            :show-service-filter="true"
            @update:search="setSearch"
            @update:status-filter="setStatusFilter"
            @update:service-filter="setServiceFilter"
          />

          <!-- Client items list -->
          <div class="space-y-2 pt-1">
            <button
              v-for="client in paginatedClients"
              :key="client.client_id"
              type="button"
              class="w-full flex items-center justify-between gap-3 rounded-[1rem] p-3 text-left transition hover:-translate-y-0.5"
              :class="activeClientId === client.client_id
                ? 'border border-[#8eb4ff] bg-[#eaf3fb]/80 ring-2 ring-[#8eb4ff]/40 shadow-[0_4px_12px_rgba(47,111,171,0.08)]'
                : 'border border-[#eef2f7] hover:border-[#bfd0e6] bg-[#fbfdff] hover:bg-white'"
              @click="selectClient(client)"
            >
              <div class="flex items-center gap-3 min-w-0">
                <div
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-black transition"
                  :class="activeClientId === client.client_id
                    ? 'bg-[#2f6fab] text-white'
                    : 'bg-[#eaf3fb] text-[#2f6fab]'"
                >
                  {{ getClientInitials(client.company_name) }}
                </div>

                <div class="min-w-0">
                  <p class="truncate text-[0.92rem] font-extrabold text-[#0f172a]">
                    {{ client.company_name }}
                  </p>
                  <span class="inline-flex rounded-full bg-[#eaf3fb] px-2 py-0.5 text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab] mt-0.5">
                    {{ client.client_id }}
                  </span>
                </div>
              </div>

              <span
                class="shrink-0 rounded-full px-2.5 py-1 text-[0.7rem] font-extrabold uppercase tracking-[0.08em] transition"
                :class="activeClientId === client.client_id
                  ? 'bg-[#2f6fab] text-white'
                  : 'bg-[#eef4ff] text-[#3556a8]'"
              >
                {{ generatedReportsCount(client) }}/{{ displaySlotCount(client) }} ready
              </span>
            </button>

            <div
              v-if="!paginatedClients.length"
              class="rounded-[1rem] border border-dashed border-[#d8e4f0] bg-white p-6 text-center text-sm text-[#64748b]"
            >
              No clients match the current filters.
            </div>
          </div>

          <!-- Bottom Pagination -->
          <ReportPagination
            v-if="filteredClients.length"
            :current-page="currentPage"
            :total-pages="totalPages"
            :total-items="filteredClients.length"
            :page-size="pageSize"
            @update:current-page="currentPage = $event"
          />
        </aside>

        <!-- Right Column: Selected Client Monthly Reports Details -->
        <main
          class="space-y-5 min-w-0"
          :class="{ 'hidden lg:block': !mobileShowingDetails && !activeClient }"
        >
          <!-- Mobile Back Button -->
          <div
            v-if="activeClient"
            class="lg:hidden"
          >
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-[0.85rem] border border-[#d7e2ef] bg-[#f6f9ff] px-3.5 py-2 text-xs font-extrabold text-[#2855a6] transition hover:-translate-y-0.5"
              @click="backToClientsList"
            >
              <ArrowLeft class="h-3.5 w-3.5" />
              Back to clients
            </button>
          </div>

          <!-- When a client IS selected -->
          <template v-if="activeClient">
            <!-- Client Overview Header Card -->
            <section class="flex flex-col gap-4 rounded-[1.4rem] border border-[#d8e4f0] bg-white p-5 sm:p-6 shadow-[0_10px_22px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-center gap-4 min-w-0">
                <div class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#eaf3fb] text-lg font-black text-[#2f6fab]">
                  {{ getClientInitials(activeClient.company_name) }}
                </div>
                <div class="min-w-0">
                  <div class="flex items-center gap-2.5 flex-wrap">
                    <h2 class="truncate text-[1.4rem] font-black text-[#0f172a]">
                      {{ activeClient.company_name }}
                    </h2>
                    <span class="inline-flex rounded-full bg-[#eaf3fb] px-3 py-1 text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab]">
                      {{ activeClient.client_id }}
                    </span>
                  </div>
                  <p class="mt-1 text-[0.9rem] font-medium text-[#64748b]">
                    Monthly performance reports
                  </p>
                </div>
              </div>

              <div class="flex flex-col sm:items-end gap-2 shrink-0">
                <div class="text-[0.92rem] font-extrabold text-[#0f172a]">
                  {{ activeClientGeneratedCount }} of {{ activeClientTotalSlots }} ready
                </div>
                <div class="h-2.5 w-44 rounded-full bg-[#e2e8f0] overflow-hidden">
                  <div
                    class="h-full rounded-full bg-[#173b5f] transition-all duration-500"
                    :style="{ width: activeClientProgressPercentage + '%' }"
                  />
                </div>
              </div>
            </section>

            <!-- Monthly Reports List Section -->
            <section class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <h2 class="text-[1.25rem] font-extrabold text-[#0f172a]">
                    Monthly reports
                  </h2>
                  <p class="text-[0.9rem] text-[#64748b]">
                    One report for each month.
                  </p>
                </div>

                <button
                  v-if="canGenerateReports"
                  type="button"
                  class="action-btn action-btn--light"
                  @click="openCustomSlotModal"
                >
                  <Plus class="h-4 w-4" />
                  Custom
                </button>
              </div>

              <!-- List of Month Rows -->
              <div class="space-y-3">
                <MonthlyReportCard
                  v-for="slot in activeClientSlots"
                  :key="`${activeClient.client_id}-${slot.month_number}`"
                  :month-slot="slot"
                  :can-generate="canGenerateReports && (!slot.report || slot.status === 'draft')"
                  :can-view="!!slot.report && ['generated', 'published'].includes(String(slot.report.status || slot.status).toLowerCase())"
                  :can-download="false"
                  :can-delete="canGenerateReports"
                  :can-upload="canGenerateReports"
                  :can-update="!!slot.report?.can_update && canGenerateReports"
                  :show-html-actions="false"
                  :empty-message="'No report has been generated for this month yet.'"
                  :status-meta="slotStatusMeta(slot)"
                  :is-custom="slot.month_number > 3"
                  @generate="openBuilder(activeClient, slot)"
                  @view="openPreviewById(slot.report?.id || null)"
                  @download-pdf="openPdfById(slot.report?.id || null)"
                  @download-html="openHtmlById(slot.report?.id || null)"
                  @upload="openUpload(activeClient, slot)"
                  @update-report="openUpdateUpload(activeClient, slot)"
                  @delete-report="confirmDeleteReport(slot.report)"
                />
              </div>

              <p class="flex items-center gap-1.5 pt-1 text-[0.85rem] text-[#64748b]">
                <Info class="h-4 w-4 text-[#94a3b8]" />
                Custom months appear at the bottom of this list.
              </p>
            </section>
          </template>

          <!-- Empty State when no client is selected -->
          <div
            v-else
            class="flex flex-col items-center justify-center rounded-[1.4rem] border border-dashed border-[#d8e4f0] bg-white p-12 text-center shadow-[0_10px_22px_rgba(15,23,42,0.04)] min-h-[460px]"
          >
            <div class="flex h-16 w-16 items-center justify-center rounded-[1.2rem] bg-[#eaf3fb] text-[#2f6fab] mb-4">
              <FileText class="h-8 w-8" />
            </div>
            <h3 class="text-[1.25rem] font-black text-[#0f172a]">
              Select a Client
            </h3>
            <p class="mt-2 max-w-sm text-[0.92rem] text-[#64748b] leading-relaxed">
              Choose a client from the list on the left to review their monthly performance, generate reports, and copy share links.
            </p>
          </div>
        </main>
      </div>
    </div>

    <!-- Custom Slot Configuration Modal -->
    <div
      v-if="customSlotModalOpen && activeClient"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div class="w-full max-w-sm rounded-[1.4rem] bg-white p-6 shadow-2xl border border-[#d8e4f0] space-y-4">
        <div class="flex items-center justify-between pb-3 border-b border-[#eef2f7]">
          <h3 class="text-[1.05rem] font-extrabold text-[#0f172a]">
            Monthly Slots Setup
          </h3>
          <button
            type="button"
            class="text-[#94a3b8] hover:text-[#475569]"
            @click="customSlotModalOpen = false"
          >
            <X class="h-5 w-5" />
          </button>
        </div>

        <p class="text-[0.88rem] text-[#64748b]">
          Configure how many monthly report slots to display for <span class="font-bold text-[#0f172a]">{{ activeClient.company_name }}</span>.
        </p>

        <div class="space-y-2">
          <label class="block text-[0.72rem] font-extrabold text-[#748197] uppercase tracking-[0.08em]">
            Custom Month Count (1 - 12)
          </label>
          <input
            v-model.number="customSlotCountInput"
            type="number"
            min="1"
            max="12"
            class="w-full rounded-[0.85rem] border border-[#d3dce9] px-3.5 py-2.5 text-sm font-semibold text-[#243049] outline-none focus:border-[#8eb4ff] focus:ring-2 focus:ring-[#cfe0ff]"
          >
        </div>

        <div class="flex justify-end gap-2.5 pt-3 border-t border-[#eef2f7]">
          <button
            type="button"
            class="action-btn action-btn--light !py-2 !px-3.5 !text-[0.85rem]"
            @click="customSlotModalOpen = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="action-btn action-btn--dark !py-2 !px-3.5 !text-[0.85rem]"
            @click="applyCustomSlotPreference"
          >
            Apply
          </button>
        </div>
      </div>
    </div>

    <ReportBuilderModal
      v-model:open="builderOpen"
      :clients="clients"
      :selected-client="selectedClient"
      :selected-month-number="selectedMonthNumber"
      :report="selectedReport"
      :mode="selectedReport ? 'edit' : 'create'"
      :loading="builderLoading"
      :error="builderError"
      @generate="handleGenerate"
    />

    <ReportGenerationLoadingModal
      :open="isGeneratingReport"
      :client-name="generationClientName"
      :month-label="generationMonthLabel"
      :error="generationError"
      @dismiss="handleDismissGeneration"
    />

    <ReportPreviewDialog
      v-model:open="isReportPreviewOpen"
      :report="selectedPreviewReport"
      :client="selectedPreviewClient"
      :role="currentUserRole"
      :loading="isReportPreviewLoading"
      :error="reportPreviewError"
      :html="reportPreviewHtml"
      :can-edit="canGenerateReports"
      :can-ai-edit="canAiEditReports"
      :can-open-full-page="canViewReports"
      :can-download="canViewReports || canGenerateReports"
      :can-delete="canGenerateReports"
      :downloading="isDownloadingPreviewPdf"
      @edit-report="editSelectedPreviewReport"
      @open-full-page="openSelectedPreviewFullPage"
      @download-pdf="handleDownloadPreviewPdf"
      @delete-report="confirmDeleteReport(selectedPreviewReport)"
      @refresh-report="refreshReportPreview"
    />

    <ReportQuickEditDialog
      v-model:open="quickEditOpen"
      :report="selectedQuickEditReport"
      :loading="quickEditLoading"
      :error="quickEditError"
      @save="handleQuickEditSave"
    />

    <UploadReportModal
      v-model:open="uploadOpen"
      :mode="uploadMode"
      :client-id="uploadClient?.client_id || ''"
      :client-code="uploadClient?.client_id"
      :client-name="uploadClient?.company_name || ''"
      :month-number="uploadSlot?.month_number || 1"
      :month-label="uploadSlot?.report_month_label || ''"
      :date-from="uploadSlot?.report?.date_from"
      :date-to="uploadSlot?.report?.date_to"
      :existing-report="uploadSlot?.report"
      @uploaded="handleUploaded"
      @updated="handleUploaded"
    />

    <ConfirmDialog
      :open="deleteConfirmOpen"
      title="Delete Monthly Report"
      :description="deleteConfirmDescription"
      btn-text="Delete Report"
      btn-color="bg-[#dc2626] hover:bg-[#b91c1c]"
      :busy="isDeletingReport"
      busy-text="Deleting..."
      @update:open="deleteConfirmOpen = $event"
      @action="handleConfirmDeleteReport"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import {
  ArrowLeft,
  FileText,
  Info,
  Plus,
  RefreshCw,
  X,
} from "lucide-vue-next";
import AnimatedList from "@/components/AnimatedList.vue";
import AnimatedItem from "@/components/AnimatedItem.vue";
import { useAuthStore } from "@/store/auth";
import { useAccessControl } from "@/composables/useAccessControl";
import { ACCESS_PERMISSION_KEYS } from "@/constants/accessControl";
import ClientReportsView from "@/components/reports/ClientReportsView.vue";
import MonthlyReportCard from "@/components/reports/MonthlyReportCard.vue";
import ReportBuilderModal from "@/components/reports/ReportBuilderModal.vue";
import ReportGenerationLoadingModal from "@/components/reports/ReportGenerationLoadingModal.vue";
import ReportFilters from "@/components/reports/ReportFilters.vue";
import ReportPreviewDialog from "@/components/reports/ReportPreviewDialog.vue";
import ReportQuickEditDialog from "@/components/reports/ReportQuickEditDialog.vue";
import ReportPagination from "@/components/reports/ReportPagination.vue";
import UploadReportModal from "@/components/reports/UploadReportModal.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useToast } from "vue-toast-notification";
import {
  createMonthlyReport,
  fetchReportPreviewHtml,
  getReportClients,
  openReportPdf,
  openReportHtml,
  printReportHtml,
  publishMonthlyReport,
  updateMonthlyReport,
  openUploadedReportPdf,
  deleteMonthlyReport,
} from "@/services/reportsService";
import type {
  GenerateMonthlyReportForm,
  ReportClientCard,
  ReportMonthSlot,
  ReportRecord,
  ReportSlotMode,
  ReportStatusMeta,
  ReportStatusTone,
} from "@/types/reports";

type ReportsPageRole = "super_admin" | "admin" | "member" | "client" | "guest";
type ReportStatusFilter = "all" | "none" | "partial" | "full";

interface SlotPreference {
  mode: ReportSlotMode;
  count: number;
}

const SLOT_PREF_KEY = "dmsuite.reports.slot-preferences";
const pageSize = 10;

const auth = useAuthStore();
const { canPermission } = useAccessControl();

const clients = ref<ReportClientCard[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const builderOpen = ref(false);
const builderLoading = ref(false);
const builderError = ref<string | null>(null);
const selectedClient = ref<ReportClientCard | null>(null);
const selectedReport = ref<ReportRecord | null>(null);
const selectedMonthNumber = ref<number | null>(null);
const selectedPreviewReport = ref<ReportRecord | null>(null);
const selectedPreviewClient = ref<ReportClientCard | null>(null);
const isReportPreviewOpen = ref(false);
const isReportPreviewLoading = ref(false);
const reportPreviewHtml = ref("");
const reportPreviewError = ref<string | null>(null);
const quickEditOpen = ref(false);
const quickEditLoading = ref(false);
const quickEditError = ref<string | null>(null);
const selectedQuickEditReport = ref<ReportRecord | null>(null);
const selectedQuickEditClient = ref<ReportClientCard | null>(null);
const uploadOpen = ref(false);
const uploadMode = ref<"upload" | "update">("upload");
const uploadClient = ref<ReportClientCard | null>(null);
const uploadSlot = ref<ReportMonthSlot | null>(null);
const isGeneratingReport = ref(false);
const generationClientName = ref("");
const generationMonthLabel = ref("");
const generationError = ref<string | null>(null);
const isDownloadingPreviewPdf = ref(false);
const search = ref("");
const statusFilter = ref<ReportStatusFilter>("all");
const serviceFilter = ref("");
const currentPage = ref(1);
const expandedClientId = ref<string | null>(null);
const slotPreferences = reactive<Record<string, SlotPreference>>({});
const previewHtmlCache = reactive<Record<number, string>>({});
let previewRequestToken = 0;

// Master-detail state
const activeClientId = ref<string | null>(null);
const mobileShowingDetails = ref(false);
const customSlotModalOpen = ref(false);
const customSlotCountInput = ref(3);

const toast = useToast();
const deleteConfirmOpen = ref(false);
const isDeletingReport = ref(false);
const reportToDelete = ref<ReportRecord | null>(null);

const activeClient = computed(() => {
  if (!activeClientId.value) return null;
  return clients.value.find((c) => c.client_id === activeClientId.value) ?? null;
});

const activeClientSlots = computed(() => {
  return activeClient.value ? displaySlots(activeClient.value) : [];
});

const activeClientGeneratedCount = computed(() => {
  return activeClient.value ? generatedReportsCount(activeClient.value) : 0;
});

const activeClientTotalSlots = computed(() => {
  return activeClient.value ? displaySlotCount(activeClient.value) : 0;
});

const activeClientProgressPercentage = computed(() => {
  if (!activeClientTotalSlots.value) return 0;
  return Math.min(
    100,
    Math.round(
      (activeClientGeneratedCount.value / activeClientTotalSlots.value) * 100
    )
  );
});

function selectClient(client: ReportClientCard) {
  activeClientId.value = client.client_id;
  mobileShowingDetails.value = true;
}

function backToClientsList() {
  mobileShowingDetails.value = false;
}

function openCustomSlotModal() {
  if (!activeClient.value) return;
  const pref = slotPreference(activeClient.value.client_id);
  customSlotCountInput.value = pref.count;
  customSlotModalOpen.value = true;
}

function applyCustomSlotPreference() {
  if (!activeClient.value) return;
  const clientId = activeClient.value.client_id;
  updateSlotCount(clientId, customSlotCountInput.value);
  customSlotModalOpen.value = false;
  toast.success("Updated monthly report slots.");
}

function getClientInitials(name: string): string {
  if (!name) return "CL";
  const clean = name.trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  const stopWords = new Set(["the", "of", "and", "for", "in", "to", "a", "an"]);
  const significant = words.filter((w) => !stopWords.has(w.toLowerCase()));
  if (significant.length >= 2) {
    return (significant[0][0] + significant[1][0]).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

const deleteConfirmDescription = computed(() => {
  if (!reportToDelete.value) {
    return "Are you sure you want to delete this report? This action cannot be undone.";
  }
  const month = reportToDelete.value.report_month_label || (reportToDelete.value.month_number ? `Month ${reportToDelete.value.month_number}` : "this month");
  const clientCode = reportToDelete.value.client_id;
  return `Are you sure you want to delete the ${month} report for client ${clientCode}? This will permanently remove the report, its data, and associated files.`;
});

const currentUserRole = computed<ReportsPageRole>(() => {
  if (auth.accountType === "client") return "client";
  if (auth.accountType !== "internal_user") return "guest";
  if (auth.isSuperAdmin) return "super_admin";
  if (auth.isProdOrMktgAdmin) return "admin";
  return "member";
});

const canGenerateReports = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_GENERATE)
);
const canAiEditReports = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_AI_EDIT) || canGenerateReports.value
);
const canViewReports = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_VIEW)
);
const canExportReportPdf = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_EXPORT_PDF)
);
const showAdminFilters = computed(() => currentUserRole.value !== "client");

const visibleClients = computed(() => clients.value);

const serviceFilterOptions = computed(() => {
  const values = new Set<string>();

  visibleClients.value.forEach((client) => {
    if (client.package_name) values.add(client.package_name);
    client.service_names.forEach((service) => values.add(service));
  });

  return Array.from(values).sort();
});

function slotStatusTone(status: string): ReportStatusTone {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "generated" || normalized === "published") return "green";
  if (normalized === "draft") return "amber";
  if (normalized === "in_progress") return "amber";
  if (normalized === "failed") return "rose";
  if (normalized === "not_generated") return "rose";
  return "slate";
}

function reportStatusLabel(status: string): string {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "generated" || normalized === "published")
    return "Generated";
  if (normalized === "draft") return "In Progress";
  if (normalized === "in_progress") return "In Progress";
  if (normalized === "failed") return "Failed";
  if (normalized === "not_generated") return "Not Generated";
  return "Not Generated";
}

function reportStatusDescription(status: string): string {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "generated" || normalized === "published") {
    return "This monthly report is ready for review and download.";
  }
  if (normalized === "draft") {
    return "The report is still being prepared.";
  }
  if (normalized === "in_progress") {
    return "The report is currently being generated.";
  }
  if (normalized === "failed") {
    return "The last generation attempt failed.";
  }
  return "No report has been generated for this month yet.";
}

function slotStatusMeta(slot: ReportMonthSlot): ReportStatusMeta {
  if (slot.report?.source_type === "uploaded") {
    return {
      label: reportStatusLabel(slot.status),
      description: "This monthly PDF report is ready for review.",
      tone: "green",
    };
  }
  return {
    label: reportStatusLabel(slot.status),
    description: reportStatusDescription(slot.status),
    tone: slotStatusTone(slot.status),
  };
}

function isReportReadyStatus(status: string | null | undefined): boolean {
  const normalized = String(status || "").toLowerCase();
  return normalized === "generated" || normalized === "published";
}

function generatedReportsCount(client: ReportClientCard): number {
  return displaySlots(client).filter((slot) =>
    isReportReadyStatus(slot.report?.status || slot.status)
  ).length;
}

function highestGeneratedMonthNumber(client: ReportClientCard): number {
  return client.max_generated_month_number ?? 0;
}

function slotPreference(clientId: string): SlotPreference {
  const stored = slotPreferences[clientId];
  if (stored) return stored;
  return {
    mode: "3",
    count: 3,
  };
}

function resolveBaseSlotCount(client: ReportClientCard): number {
  const preference = slotPreference(client.client_id);
  const highestGeneratedMonth = highestGeneratedMonthNumber(client);
  const base =
    preference.mode === "custom" ? preference.count : Number(preference.mode);

  if (!Number.isFinite(base) || base < 1) {
    return Math.max(1, highestGeneratedMonth);
  }

  return Math.max(base, highestGeneratedMonth, 1);
}

function displaySlotCount(client: ReportClientCard): number {
  return resolveBaseSlotCount(client);
}

function displaySlots(client: ReportClientCard): ReportMonthSlot[] {
  const visibleCount = displaySlotCount(client);
  const byMonth = new Map<number, ReportMonthSlot>();

  client.months.forEach((slot) => {
    byMonth.set(slot.month_number, slot);
  });

  return Array.from({ length: visibleCount }, (_, index) => {
    const monthNumber = index + 1;
    return (
      byMonth.get(monthNumber) ?? {
        month_number: monthNumber,
        report_month_label: `Month ${monthNumber}`,
        status: "not_generated",
        version: 0,
        last_generated_at: null,
        report: null,
      }
    );
  });
}

const filteredClients = computed(() => {
  const query = search.value.trim().toLowerCase();

  return visibleClients.value.filter((client) => {
    const matchesSearch =
      !query ||
      client.company_name.toLowerCase().includes(query) ||
      client.client_id.toLowerCase().includes(query) ||
      (client.package_name || "").toLowerCase().includes(query) ||
      client.service_names.some((service) =>
        service.toLowerCase().includes(query)
      );

    const slots = displaySlots(client);
    const generatedCount = slots.filter((slot) =>
      isReportReadyStatus(slot.report?.status || slot.status)
    ).length;
    const totalSlots = slots.length;

    const matchesStatus =
      statusFilter.value === "all" ||
      (statusFilter.value === "none" && generatedCount === 0) ||
      (statusFilter.value === "partial" &&
        generatedCount > 0 &&
        generatedCount < totalSlots) ||
      (statusFilter.value === "full" &&
        totalSlots > 0 &&
        generatedCount >= totalSlots);

    const matchesService =
      !serviceFilter.value ||
      client.package_name === serviceFilter.value ||
      client.service_names.includes(serviceFilter.value);

    return matchesSearch && matchesStatus && matchesService;
  });
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredClients.value.length / pageSize))
);

const paginatedClients = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredClients.value.slice(start, start + pageSize);
});

const clientAccount = computed(() => visibleClients.value[0] ?? null);

const clientDisplaySlots = computed(() =>
  clientAccount.value ? displaySlots(clientAccount.value) : []
);

function setSearch(value: string) {
  search.value = value;
}

function setStatusFilter(value: string) {
  statusFilter.value = value as ReportStatusFilter;
}

function setServiceFilter(value: string) {
  serviceFilter.value = value;
}

function updateSlotMode(clientId: string, value: ReportSlotMode) {
  const current = slotPreference(clientId);
  slotPreferences[clientId] = {
    mode: value,
    count: value === "custom" ? Math.max(current.count, 1) : Number(value),
  };
  persistSlotPreferences();
}

function updateSlotCount(clientId: string, value: number) {
  const safeValue = Number.isFinite(value)
    ? Math.min(Math.max(Math.floor(value), 1), 12)
    : 1;
  const current = slotPreference(clientId);
  slotPreferences[clientId] = {
    mode: "custom",
    count: safeValue,
  };
  if (current.mode !== "custom" || current.count !== safeValue) {
    persistSlotPreferences();
  }
}

function persistSlotPreferences() {
  try {
    localStorage.setItem(SLOT_PREF_KEY, JSON.stringify(slotPreferences));
  } catch {
    /* ignore storage failures */
  }
}

function loadSlotPreferences() {
  try {
    const raw = localStorage.getItem(SLOT_PREF_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, SlotPreference>;
    Object.entries(parsed).forEach(([clientId, preference]) => {
      if (!preference || typeof preference !== "object") return;
      const mode = ["1", "2", "3", "custom"].includes(preference.mode)
        ? preference.mode
        : "3";
      const count = Number.isFinite(preference.count)
        ? Math.min(Math.max(Math.floor(preference.count), 1), 12)
        : 3;
      slotPreferences[clientId] = {
        mode: mode as ReportSlotMode,
        count,
      };
    });
  } catch {
    /* ignore storage failures */
  }
}

function isClientExpanded(clientId: string) {
  return expandedClientId.value === clientId;
}

function toggleClient(clientId: string) {
  expandedClientId.value =
    expandedClientId.value === clientId ? null : clientId;
}

function monthSlotFor(client: ReportClientCard, monthNumber: number) {
  return (
    displaySlots(client).find((slot) => slot.month_number === monthNumber) ??
    null
  );
}

function reportById(reportId: number | null) {
  if (!reportId) return null;
  for (const client of visibleClients.value) {
    const slot = client.months.find((item) => item.report?.id === reportId);
    if (slot?.report) return slot.report;
  }
  return null;
}

function openBuilder(
  client?: ReportClientCard | null,
  slotOrReport?: ReportMonthSlot | ReportRecord | null
) {
  selectedClient.value = client || null;
  selectedReport.value = slotOrReport
    ? "report" in slotOrReport
      ? slotOrReport.report || null
      : "preview_html_url" in slotOrReport || "preview_url" in slotOrReport
        ? slotOrReport
        : null
    : null;
  selectedMonthNumber.value = slotOrReport ? slotOrReport.month_number : null;
  builderError.value = null;
  builderOpen.value = true;
}

function openUpload(client: ReportClientCard, slot: ReportMonthSlot | null) {
  if (!slot) return;
  uploadClient.value = client;
  uploadSlot.value = slot;
  uploadMode.value = "upload";
  uploadOpen.value = true;
}

function openUpdateUpload(client: ReportClientCard, slot: ReportMonthSlot | null) {
  if (!slot?.report) return;
  uploadClient.value = client;
  uploadSlot.value = slot;
  uploadMode.value = "update";
  uploadOpen.value = true;
}

async function handleUploaded(report: ReportRecord) {
  await refresh();
  const context = reportContextById(report.id);
  if (context) uploadSlot.value = displaySlots(context.client as ReportClientCard).find((slot) => slot.report?.id === report.id) || uploadSlot.value;
}

async function loadPreview(
  report: ReportRecord,
  client: ReportClientCard | null
) {
  selectedPreviewReport.value = report;
  selectedPreviewClient.value = client;
  reportPreviewError.value = null;
  isReportPreviewOpen.value = true;

  if (previewHtmlCache[report.id]) {
    reportPreviewHtml.value = previewHtmlCache[report.id];
    isReportPreviewLoading.value = false;
    return;
  }

  reportPreviewHtml.value = "";
  isReportPreviewLoading.value = true;

  const requestToken = ++previewRequestToken;

  try {
    const html = await fetchReportPreviewHtml(report);
    if (requestToken !== previewRequestToken) return;
    if (!String(html || "").trim()) {
      reportPreviewError.value = "The generated preview was empty. Please try reopening the report.";
      reportPreviewHtml.value = "";
      return;
    }
    previewHtmlCache[report.id] = html;
    reportPreviewHtml.value = html;
  } catch (err: any) {
    if (requestToken !== previewRequestToken) return;
    reportPreviewError.value =
      err?.response?.data?.message || "Unable to load the report preview.";
  } finally {
    if (requestToken === previewRequestToken) {
      isReportPreviewLoading.value = false;
    }
  }
}

async function refreshReportPreview() {
  if (!selectedPreviewReport.value) return;
  const report = selectedPreviewReport.value;
  delete previewHtmlCache[report.id];
  try {
    const html = await fetchReportPreviewHtml(report);
    previewHtmlCache[report.id] = html;
    reportPreviewHtml.value = html;
  } catch (err: any) {
    reportPreviewError.value =
      err?.response?.data?.message || "Unable to refresh the report preview.";
  }
}

function openPreviewById(reportId: number | null) {
  const context = reportContextById(reportId);
  if (!context) return;
  if (context.report.source_type === "uploaded") {
    void openUploadedReportPdf(context.report).catch((err: any) => {
      error.value =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to open the uploaded PDF.";
    });
    return;
  }
  void loadPreview(context.report, context.client);
}

function editSelectedPreviewReport() {
  if (!selectedPreviewReport.value || !selectedPreviewClient.value) return;
  isReportPreviewOpen.value = false;
  selectedQuickEditReport.value = selectedPreviewReport.value;
  selectedQuickEditClient.value = selectedPreviewClient.value;
  quickEditError.value = null;
  quickEditOpen.value = true;
}

async function handleQuickEditSave(fields: {
  title: string;
  prepared_by: string;
  executive_snapshot_note: string;
  overall_recommendation_summary: string;
  sections: Record<string, {
    enabled: boolean;
    summary?: string;
    insight: string;
    recommendation?: string;
  }>;
}) {
  if (!selectedQuickEditReport.value) return;

  quickEditLoading.value = true;
  quickEditError.value = null;

  try {
    const report = selectedQuickEditReport.value;
    const existingData = (report.report_data || {}) as Record<string, any>;
    const mergedSections = { ...(existingData.sections || {}) };

    Object.entries(fields.sections || {}).forEach(([secKey, secVal]) => {
      mergedSections[secKey] = {
        ...(mergedSections[secKey] || {}),
        ...(secVal.summary !== undefined ? { summary: secVal.summary } : {}),
        insight: secVal.insight,
        recommendation: secVal.recommendation,
      };
    });

    const payload: GenerateMonthlyReportForm = {
      client_id: report.client_id,
      month_number: report.month_number,
      report_month_label: report.report_month_label,
      title: fields.title,
      date_from: report.date_from || "",
      date_to: report.date_to || "",
      selected_sections: report.selected_sections || [],
      report_data_json: JSON.stringify({
        ...existingData,
        prepared_by: fields.prepared_by,
        executive_snapshot_note: fields.executive_snapshot_note,
        overall_recommendation_summary: fields.overall_recommendation_summary,
        sections: mergedSections,
        social_media_insight: fields.sections?.socialMediaPerformance?.insight || existingData.social_media_insight,
        social_media_recommendation: fields.sections?.socialMediaPerformance?.recommendation || existingData.social_media_recommendation,
        ai_chatbot_insight: fields.sections?.aiChatbotManagement?.insight || existingData.ai_chatbot_insight,
        ai_chatbot_recommendation: fields.sections?.aiChatbotManagement?.recommendation || existingData.ai_chatbot_recommendation,
        ai_podcast_insight: fields.sections?.aiPodcastProgress?.insight || existingData.ai_podcast_insight,
        ai_podcast_recommendation: fields.sections?.aiPodcastProgress?.recommendation || existingData.ai_podcast_recommendation,
        seo_ads_insight: fields.sections?.seoManagement?.insight || existingData.seo_ads_insight,
        seo_summary: fields.sections?.seoManagement?.summary || existingData.seo_summary,
        seo_recommendation: fields.sections?.seoManagement?.recommendation || existingData.seo_recommendation,
        blog_insight: fields.sections?.blogManagement?.insight || existingData.blog_insight,
        blog_recommendation: fields.sections?.blogManagement?.recommendation || existingData.blog_recommendation,
      }),
      publish: report.status === "published",
      status:
        report.status === "published"
          ? "published"
          : report.status === "draft"
            ? "draft"
            : report.status === "archived"
              ? "archived"
              : "generated",
      version: report.version,
    };

    const savedReport = await updateMonthlyReport(report.id, payload, {
      cover_assets: [],
      social_media_assets: [],
    });

    delete previewHtmlCache[report.id];
    await refresh();
    quickEditOpen.value = false;
    selectedQuickEditReport.value = savedReport;
    await loadPreview(savedReport, selectedQuickEditClient.value);
  } catch (err: any) {
    quickEditError.value =
      err?.response?.data?.message || "Unable to save report changes.";
  } finally {
    quickEditLoading.value = false;
  }
}

function openPdfById(reportId: number | null) {
  const report = reportById(reportId);
  if (report) {
    if (report.source_type === "uploaded") {
      void openUploadedReportPdf(report);
    } else {
      void printReportHtml(report);
    }
  }
}

function openHtmlById(reportId: number | null) {
  const report = reportById(reportId);
  if (report) {
    void openReportHtml(report).catch((err: any) => {
      error.value =
        err?.response?.data?.message || err?.message || "Unable to open the report page.";
    });
  }
}

function openSelectedPreviewFullPage() {
  if (selectedPreviewReport.value) {
    const preloaded =
      reportPreviewHtml.value ||
      previewHtmlCache[selectedPreviewReport.value.id] ||
      "";
    void openReportHtml(selectedPreviewReport.value, preloaded).catch((err: any) => {
      console.warn("Unable to open the full report page:", err);
    });
  }
}

function confirmDeleteReport(report: ReportRecord | null) {
  if (!report) return;
  reportToDelete.value = report;
  deleteConfirmOpen.value = true;
}

async function handleConfirmDeleteReport() {
  if (!reportToDelete.value) return;
  const targetId = reportToDelete.value.id;
  try {
    isDeletingReport.value = true;
    await deleteMonthlyReport(targetId);
    toast.success("Report deleted successfully.");
    delete previewHtmlCache[targetId];
    deleteConfirmOpen.value = false;
    if (selectedPreviewReport.value?.id === targetId) {
      isReportPreviewOpen.value = false;
      selectedPreviewReport.value = null;
    }
    reportToDelete.value = null;
    await refresh();
  } catch (err: any) {
    const message = err?.response?.data?.message || err?.message || "Failed to delete report.";
    toast.error(message);
  } finally {
    isDeletingReport.value = false;
  }
}

function expandedEmptyMessage(): string {
  if (canGenerateReports.value) {
    return "No reports have been generated for this client yet.";
  }

  if (currentUserRole.value === "member") {
    return "Reports are not available yet.";
  }

  if (currentUserRole.value === "client") {
    return "Reports are not available yet.";
  }

  return "No reports have been generated for this client yet.";
}

async function refresh() {
  loading.value = true;
  error.value = null;
  try {
    const response = await getReportClients();
    clients.value = response;

    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
    }
  } catch (err: any) {
    error.value = err?.response?.data?.message || "Unable to load reports.";
  } finally {
    loading.value = false;
  }
}

function reportContextById(
  reportId: number | null
): { report: ReportRecord; client: ReportClientCard | null } | null {
  if (!reportId) return null;

  for (const client of visibleClients.value) {
    const slot = client.months.find((item) => item.report?.id === reportId);
    if (slot?.report) {
      return { report: slot.report, client };
    }
  }

  return null;
}

function handleDismissGeneration() {
  isGeneratingReport.value = false;
  if (generationError.value) {
    builderOpen.value = true;
    builderError.value = generationError.value;
  }
}

async function handleDownloadPreviewPdf() {
  if (!selectedPreviewReport.value) return;
  isDownloadingPreviewPdf.value = true;
  try {
    if (selectedPreviewReport.value.source_type === "uploaded") {
      await openUploadedReportPdf(selectedPreviewReport.value);
    } else {
      await printReportHtml(selectedPreviewReport.value);
    }
  } catch (err: any) {
    reportPreviewError.value =
      err?.response?.data?.message || err?.message || "Unable to download the PDF report.";
  } finally {
    isDownloadingPreviewPdf.value = false;
  }
}

async function handleGenerate(payload: {
  payload: GenerateMonthlyReportForm;
  files: Record<string, File[]>;
  action: "draft" | "html" | "pdf" | "generate";
  reportId?: number | null;
  extractedPayload?: Record<string, string>;
}) {
  if (payload.action === "draft") {
    builderLoading.value = true;
    builderError.value = null;

    try {
      await (payload.reportId
        ? updateMonthlyReport(
            payload.reportId,
            payload.payload,
            payload.files,
            payload.extractedPayload
          )
        : createMonthlyReport(payload.payload, payload.files, payload.extractedPayload));

      await refresh();
      builderOpen.value = false;
    } catch (err: any) {
      builderError.value =
        err?.response?.data?.message || err?.message || "Unable to save report draft.";
    } finally {
      builderLoading.value = false;
    }
    return;
  }

  const targetClient = clients.value.find((c) => c.client_id === payload.payload.client_id);
  generationClientName.value = targetClient?.company_name || "Client";
  generationMonthLabel.value = `Month ${payload.payload.month_number}`;
  generationError.value = null;
  isGeneratingReport.value = true;
  builderOpen.value = false;

  try {
    const savedReport = payload.reportId
      ? await updateMonthlyReport(
          payload.reportId,
          payload.payload,
          payload.files,
          payload.extractedPayload
        )
      : await createMonthlyReport(payload.payload, payload.files, payload.extractedPayload);

    if (payload.action === "pdf") {
      const publishedReport = await publishMonthlyReport(savedReport.id);
      await refresh();
      isGeneratingReport.value = false;
      await printReportHtml(publishedReport);
      return;
    }

    if (payload.action === "html" || payload.action === "generate") {
      const reportToPreview =
        payload.action === "generate"
          ? await publishMonthlyReport(savedReport.id)
          : savedReport;

      // Invalidate preview cache so fresh report is loaded
      delete previewHtmlCache[reportToPreview.id];

      await refresh();
      isGeneratingReport.value = false;
      await loadPreview(reportToPreview, selectedClient.value);
      return;
    }
  } catch (err: any) {
    generationError.value =
      err?.response?.data?.message || "Unable to generate the report.";
  }
}

watch([search, serviceFilter, statusFilter], () => {
  currentPage.value = 1;
});

watch(
  () => paginatedClients.value.map((client) => client.client_id).join("|"),
  () => {
    if (
      expandedClientId.value &&
      !paginatedClients.value.some(
        (client) => client.client_id === expandedClientId.value
      )
    ) {
      expandedClientId.value = null;
    }
  }
);

watch(
  () => filteredClients.value.length,
  () => {
    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
    }
  }
);

onMounted(() => {
  loadSlotPreferences();
  refresh();
});

watch(isReportPreviewOpen, (open) => {
  if (!open) {
    previewRequestToken += 1;
    isReportPreviewLoading.value = false;
  }
});

watch(quickEditOpen, (open) => {
  if (!open) {
    quickEditLoading.value = false;
    quickEditError.value = null;
  }
});
</script>

<style scoped>
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border-radius: 0.95rem;
  padding: 0.72rem 1rem;
  font-size: 0.9rem;
  font-weight: 800;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn--light {
  border: 1px solid #d7e2ef;
  background: #f6f9ff;
  color: #2855a6;
}

.action-btn--dark {
  border: 1px solid #173b5f;
  background: #173b5f;
  color: #fff;
}
</style>
